'use client';

import { useEffect, useRef } from 'react';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import TypekitFonts from 'components/shared/TypekitFonts';
import HeroLanding from 'components/science/landing-v2/HeroLanding';
import PartnersStrip from 'components/science/landing-v2/PartnersStrip';
import ThreePillars from 'components/science/landing-v2/ThreePillars';
import ValidatedMetrics from 'components/science/landing-v2/ValidatedMetrics';
import PulsomicsBand from 'components/science/landing-v2/PulsomicsBand';
import AccuracyStandard from 'components/science/landing-v2/AccuracyStandard';
import DarkBridge from 'components/science/landing-v2/DarkBridge';
import ResearchLibrary from 'components/science/landing-v2/ResearchLibrary';
import OrbBeat from 'components/science/landing-v2/OrbBeat';
import EnhanceResearch from 'components/science/landing-v2/EnhanceResearch';
import ScienceFaqs from 'components/science/landing-v2/ScienceFaqs';
import type { Study } from 'content/science/studyTypes';
import { instrumentSerif } from 'utils/fonts';

import styles from './science-client.module.css';

interface ScienceLandingProps {
  studies: Study[];
  topics: { label: string; slug: string }[];
  bannerPx: number;
}

export default function ScienceLanding({ studies, topics, bannerPx }: ScienceLandingProps) {
  const ui = useGlobalUI({ headerCollaspable: false });
  const setHeaderTheme = ui.setHeaderThemeOverride;
  const headerHeightRef = useRef(ui.headerHeight ?? 76);
  headerHeightRef.current = ui.headerHeight ?? 76;
  const heroRef = useRef<HTMLDivElement>(null);

  /* The nav is dark (white, over the photo) only while the full-bleed hero sits
     behind it, and flips to the light theme the moment the hero's bottom edge
     clears the header — i.e. as the next section reaches the nav. Scoped to this
     page: the override is cleared on unmount (and on navigation by GlobalUI). */
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;
    let raf = 0;
    let last: 'dark' | 'light' | null = null;
    const update = () => {
      raf = 0;
      const next = hero.getBoundingClientRect().bottom > headerHeightRef.current ? 'dark' : 'light';
      if (next !== last) {
        last = next;
        setHeaderTheme(next);
      }
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(raf);
      setHeaderTheme(null);
    };
  }, [setHeaderTheme]);

  return (
    <main className={`${styles.page} ${instrumentSerif.variable}`}>
      <TypekitFonts kits={['dharma']} />
      <div ref={heroRef}>
        <HeroLanding bannerPx={bannerPx} />
      </div>
      <PartnersStrip />
      <ThreePillars />
      <ValidatedMetrics whitePapers={studies.filter((s) => s.kind === 'white-paper').length} />
      <PulsomicsBand />
      <DarkBridge>
        <AccuracyStandard />
        <ResearchLibrary studies={studies} />
      </DarkBridge>
      <OrbBeat topics={topics} bannerPx={bannerPx} />
      <EnhanceResearch />
      <ScienceFaqs />
    </main>
  );
}
